import { useState, useEffect, useRef } from 'react';
import { MessageSquare, Send, X, Bot, User, Trash2, Sparkles, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AICoachChat({ userId, context = null, locked = false, onLockedClick = null }) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => `session_${Date.now()}`);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const handleToggle = () => {
    if (locked) {
      if (onLockedClick) onLockedClick();
      return;
    }
    setIsOpen((v) => !v);
  };

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Load chat history on mount
  useEffect(() => {
    const loadHistory = async () => {
      try {
        const res = await fetch(`${API_URL}/api/ai900/coach/history/${userId}/${sessionId}`);
        if (res.ok) {
          const data = await res.json();
          if (data.messages?.length > 0) {
            setMessages(data.messages);
          }
        }
      } catch (error) {
        console.error('Failed to load chat history');
      }
    };
    if (userId) loadHistory();
  }, [userId, sessionId]);

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;
    
    const userMessage = inputValue.trim();
    setInputValue('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/ai900/coach/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          session_id: sessionId,
          user_id: userId,
          context: context
        })
      });

      if (!res.ok) throw new Error('Failed to get response');
      
      const data = await res.json();
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
    } catch (error) {
      toast.error('Failed to get AI response. Please try again.');
      setMessages(prev => prev.slice(0, -1)); // Remove the user message on error
    } finally {
      setIsLoading(false);
    }
  };

  const clearChat = async () => {
    try {
      await fetch(`${API_URL}/api/ai900/coach/history/${userId}/${sessionId}`, {
        method: 'DELETE'
      });
      setMessages([]);
      toast.success('Chat cleared');
    } catch (error) {
      toast.error('Failed to clear chat');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Suggested questions for new users
  const suggestedQuestions = [
    "What are the main domains covered in AI-900?",
    "Explain the difference between ML and deep learning",
    "What Azure services should I know for the exam?",
    "Give me tips for passing AI-900"
  ];

  return (
    <>
      {/* Floating Chat Button */}
      <button
        onClick={handleToggle}
        className={`fixed bottom-24 right-6 z-50 p-4 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 ${isOpen ? 'hidden' : 'flex'} items-center gap-2`}
        data-testid="ai-coach-chat-btn"
      >
        <Bot className="w-6 h-6" />
        <span className="hidden sm:inline font-medium">AI Coach{locked ? ' 🔒' : ''}</span>
        <Sparkles className="w-4 h-4 animate-pulse" />
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 z-50 w-[380px] max-w-[calc(100vw-48px)] animate-in slide-in-from-bottom-4 duration-300">
          <Card className="shadow-2xl border-violet-200">
            {/* Header */}
            <CardHeader className="bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-t-lg py-3 px-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                    <Bot className="w-5 h-5" />
                  </div>
                  <div>
                    <CardTitle className="text-base">AI Exam Coach</CardTitle>
                    <p className="text-xs text-violet-100">Powered by GPT-4o</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {messages.length > 0 && (
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8 text-white hover:bg-white/20"
                      onClick={clearChat}
                      title="Clear chat"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8 text-white hover:bg-white/20"
                    onClick={() => setIsOpen(false)}
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            </CardHeader>

            <CardContent className="p-0">
              {/* Messages Area */}
              <ScrollArea className="h-[350px] p-4" ref={scrollRef}>
                {messages.length === 0 ? (
                  <div className="space-y-4">
                    <div className="text-center py-4">
                      <div className="w-16 h-16 bg-violet-100 rounded-full mx-auto mb-3 flex items-center justify-center">
                        <Bot className="w-8 h-8 text-violet-600" />
                      </div>
                      <h3 className="font-semibold text-slate-800">Hi! I'm your AI Coach</h3>
                      <p className="text-sm text-slate-500 mt-1">
                        Ask me anything about the AI-900 exam!
                      </p>
                    </div>
                    <div className="space-y-2">
                      <p className="text-xs text-slate-400 uppercase tracking-wide">Suggested questions</p>
                      {suggestedQuestions.map((q, i) => (
                        <button
                          key={i}
                          onClick={() => { setInputValue(q); inputRef.current?.focus(); }}
                          className="w-full text-left text-sm p-2 rounded-lg bg-slate-50 hover:bg-violet-50 text-slate-600 hover:text-violet-700 transition-colors"
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {messages.map((msg, i) => (
                      <div
                        key={i}
                        className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        {msg.role === 'assistant' && (
                          <div className="w-7 h-7 bg-violet-100 rounded-full flex-shrink-0 flex items-center justify-center">
                            <Bot className="w-4 h-4 text-violet-600" />
                          </div>
                        )}
                        <div
                          className={`max-w-[80%] p-3 rounded-lg text-sm ${
                            msg.role === 'user'
                              ? 'bg-violet-600 text-white rounded-br-sm'
                              : 'bg-slate-100 text-slate-800 rounded-bl-sm'
                          }`}
                        >
                          <p className="whitespace-pre-wrap">{msg.content}</p>
                        </div>
                        {msg.role === 'user' && (
                          <div className="w-7 h-7 bg-violet-600 rounded-full flex-shrink-0 flex items-center justify-center">
                            <User className="w-4 h-4 text-white" />
                          </div>
                        )}
                      </div>
                    ))}
                    {isLoading && (
                      <div className="flex gap-2 justify-start">
                        <div className="w-7 h-7 bg-violet-100 rounded-full flex-shrink-0 flex items-center justify-center">
                          <Bot className="w-4 h-4 text-violet-600" />
                        </div>
                        <div className="bg-slate-100 p-3 rounded-lg rounded-bl-sm">
                          <Loader2 className="w-5 h-5 text-violet-600 animate-spin" />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </ScrollArea>

              {/* Input Area */}
              <div className="p-3 border-t bg-slate-50">
                <div className="flex gap-2">
                  <Input
                    ref={inputRef}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Ask about AI-900..."
                    className="flex-1 bg-white"
                    disabled={isLoading}
                    data-testid="ai-coach-input"
                  />
                  <Button
                    onClick={sendMessage}
                    disabled={!inputValue.trim() || isLoading}
                    className="bg-violet-600 hover:bg-violet-700"
                    data-testid="ai-coach-send-btn"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-slate-400 text-center mt-2">
                  AI responses are for study guidance only
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}
